import React, { useState, useEffect, useCallback } from 'react';
import { investmentPerspectivesAPI } from '../../services/api';

const FIRST_PERIOD = '2026-07';

/**
 * Manage publishing as a matrix: rows = months (latest first), columns =
 * Drummond (the '_default' group — every non-tailored client) plus each
 * tailored brand. A ticked cell means that month is visible to that audience.
 *
 * A month that was globally published (legacy "publish to all") shows every
 * cell ticked; unticking one converts it to per-target rows minus that cell.
 */
const PublishModal = ({ isOpen, onClose, period, isDrummondUser, brands }) => {
  const [months, setMonths] = useState([]);
  const [globalSet, setGlobalSet] = useState(new Set());   // legacy publish-to-all months
  const [targets, setTargets] = useState({});              // period -> [domains]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);              // `${period}|${domain}` or `${period}|row`

  const columns = [
    { domain: '_default', label: 'Drummond' },
    ...(brands || []).filter((b) => b.tailored).map((b) => ({
      domain: b.domain,
      label: b.company_name || b.domain,
    })),
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [monthsRes, pubRes] = await Promise.all([
        investmentPerspectivesAPI.getAvailableMonths(),
        investmentPerspectivesAPI.getPublishedPeriods().catch(() => ({ periods: [], targets: {} })),
      ]);
      const ms = (monthsRes.months || [])
        .filter((m) => m.value >= FIRST_PERIOD)
        .sort((a, b) => (a.value < b.value ? 1 : -1));   // latest first
      setMonths(ms);
      setGlobalSet(new Set(pubRes.periods || []));
      setTargets(pubRes.targets || {});
    } catch (err) {
      console.error('Error loading publish data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && isDrummondUser) load();
  }, [isOpen, isDrummondUser, load]);

  const isCellOn = (p, domain) => globalSet.has(p) || (targets[p] || []).includes(domain);

  // Convert a legacy globally-published month into explicit per-column targets
  // (used when unticking a single cell of an all-published month).
  const demoteGlobal = async (p, keepDomains) => {
    await investmentPerspectivesAPI.unpublishPeriod(p);              // clears global + targets
    if (keepDomains.length) await investmentPerspectivesAPI.publishPeriod(p, keepDomains);
    setGlobalSet((prev) => { const n = new Set(prev); n.delete(p); return n; });
    setTargets((prev) => ({ ...prev, [p]: keepDomains }));
  };

  const toggleCell = async (p, domain) => {
    setSaving(`${p}|${domain}`);
    try {
      if (globalSet.has(p)) {
        await demoteGlobal(p, columns.map((c) => c.domain).filter((d) => d !== domain));
      } else if ((targets[p] || []).includes(domain)) {
        await investmentPerspectivesAPI.unpublishPeriod(p, [domain]);
        setTargets((prev) => ({ ...prev, [p]: (prev[p] || []).filter((d) => d !== domain) }));
      } else {
        await investmentPerspectivesAPI.publishPeriod(p, [domain]);
        setTargets((prev) => ({ ...prev, [p]: [...(prev[p] || []), domain] }));
      }
    } catch (err) {
      console.error('Error changing publish state:', err);
    } finally {
      setSaving(null);
    }
  };

  const toggleRow = async (p) => {
    const allOn = columns.every((c) => isCellOn(p, c.domain));
    setSaving(`${p}|row`);
    try {
      if (allOn) {
        await investmentPerspectivesAPI.unpublishPeriod(p);          // full unpublish
        setGlobalSet((prev) => { const n = new Set(prev); n.delete(p); return n; });
        setTargets((prev) => ({ ...prev, [p]: [] }));
      } else {
        const missing = columns.map((c) => c.domain).filter((d) => !isCellOn(p, d));
        await investmentPerspectivesAPI.publishPeriod(p, missing);
        setTargets((prev) => ({ ...prev, [p]: [...new Set([...(prev[p] || []), ...missing])] }));
      }
    } catch (err) {
      console.error('Error toggling row publish state:', err);
    } finally {
      setSaving(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="propagate-modal-overlay" onClick={() => onClose(false)}>
      <div className="propagate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="propagate-modal-header">
          <h3>Manage Publishing</h3>
          <button className="propagate-modal-close" onClick={() => onClose(false)}>&times;</button>
        </div>

        <p className="propagate-modal-desc">
          Tick a cell to make that month visible to that audience. Drummond = every
          non-tailored client domain (they mirror the house report); tailored clients
          see their own version, so make sure their content is propagated first.
        </p>

        <div className="propagate-grid-wrapper">
          {loading ? (
            <div className="publish-loading">Loading months…</div>
          ) : (
            <table className="propagate-grid">
              <thead>
                <tr>
                  <th className="propagate-corner">Month</th>
                  {columns.map((c) => (
                    <th key={c.domain} className="propagate-col-header">{c.label}</th>
                  ))}
                  <th className="propagate-col-header" />
                </tr>
              </thead>
              <tbody>
                {months.map((m) => {
                  const p = m.value;
                  const allOn = columns.every((c) => isCellOn(p, c.domain));
                  return (
                    <tr key={p} className={p === period ? 'ip-publish-current-row' : undefined}>
                      <td className="propagate-row-label">
                        <span className="ip-publish-month">{m.label}</span>
                      </td>
                      {columns.map((c) => (
                        <td key={c.domain} className="propagate-cell">
                          <input
                            type="checkbox"
                            checked={isCellOn(p, c.domain)}
                            disabled={saving === `${p}|${c.domain}` || saving === `${p}|row`}
                            onChange={() => toggleCell(p, c.domain)}
                          />
                        </td>
                      ))}
                      <td className="propagate-cell">
                        <button
                          className="ip-publish-row-btn"
                          onClick={() => toggleRow(p)}
                          disabled={saving === `${p}|row`}
                          title={allOn ? 'Unpublish this month from everyone' : 'Publish this month to all columns'}
                        >
                          {saving === `${p}|row` ? '…' : allOn ? 'None' : 'All'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="propagate-modal-footer">
          <button className="btn-cancel" onClick={() => onClose(false)}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default PublishModal;
